import { useState, useEffect, useRef, useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { FaPlus, FaClock, FaDollarSign, FaRoute, FaUser, FaMapMarkerAlt, FaBox, FaTruck, FaCalculator, FaTimes, FaCheck, FaExclamationTriangle, FaCheckCircle, FaCopy } from 'react-icons/fa'
import { lclService, bookingService } from '../../services'
import { Card, StatusBadge, LoadingOverlay, EmptyState, Button, Input, Select, MobileCard, Modal } from '../common'
import { useChannels } from '../../hooks/useChannels'
import { useAuth } from '../../hooks/useAuth'
import { formatDateTime } from '../../utils/datetime'

// 运货方案（与渠道 shipping_method 对应）
const SHIPPING_METHODS = [
  { value: '国际空派', label: '国际空派' },
  { value: '国际海派', label: '国际海派' },
  { value: '国际小包', label: '国际小包' },
  { value: '国际快递', label: '国际快递' },
]

// 依据「运货方案 + 收件人国家」匹配渠道（用于取单价）
const matchChannel = (channels, method, receiverCountry) => {
  const byMethod = (channels || []).filter((c) => c.shipping_method === method)
  if (byMethod.length === 0) return null
  const enName = (receiverCountry || '').split('/').pop().trim().toLowerCase()
  return byMethod.find((c) => (c.destination_country || '').toLowerCase() === enName) || byMethod[0]
}

// 整柜下单：运货方式、柜型、及按柜型的指示性运费（前端演示，最终以客服验货后价格为准）
const FCL_TRANSPORT_MODES = [
  { value: '海运整柜', label: '海运整柜' },
  { value: '空运整柜', label: '空运整柜' },
]
const FCL_CONTAINER_TYPES = [
  { value: '20GP', label: '20GP' },
  { value: '40GP', label: '40GP' },
  { value: '40HQ', label: '40HQ' },
  { value: '45HQ', label: '45HQ' },
]
const FCL_RATES = {
  海运整柜: { '20GP': 8000, '40GP': 15000, '40HQ': 16000, '45HQ': 18000 },
  空运整柜: { '20GP': 45000, '40GP': 85000, '40HQ': 90000, '45HQ': 100000 },
}

// 拼柜/整柜共用下单表单；mode: 'lcl'(散货拼柜) | 'fcl'(整柜)
export default function LclOrderModule({ mode = 'lcl' } = {}) {
  const isFcl = mode === 'fcl'
  const orderTypeName = isFcl ? '整柜' : '拼柜'
  const { user, isAuthenticated } = useAuth()
  const queryClient = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [currentStep, setCurrentStep] = useState(1)
  const [estimatedPrice, setEstimatedPrice] = useState(null)

  // 行政区划级联（发件人地址）
  const [provinceOptions, setProvinceOptions] = useState([])
  const [cityOptions, setCityOptions] = useState([])
  const [districtOptions, setDistrictOptions] = useState([])
  const [streetOptions, setStreetOptions] = useState([])
  const [isMunicipality, setIsMunicipality] = useState(false)
  // 收件人目的国家选项
  const [destinationCountries, setDestinationCountries] = useState([])
  // 货物信息：物品类型/名称、敏感货提示、填写确认弹框
  const [itemTypeNames, setItemTypeNames] = useState({})
  const [sensitiveMessage, setSensitiveMessage] = useState('')
  const [cargoConfirmed, setCargoConfirmed] = useState(false)
  // 下单成功后展示的订单信息（当前仅前端演示，不写库）
  const [submittedOrder, setSubmittedOrder] = useState(null)
  // 步骤顶部锚点（切换步骤时滚动定位）
  const stepTopRef = useRef(null)
  // 记录最近一次切步的时间戳，用于拦截切步后误触发的提交
  const lastStepChangeAt = useRef(0)

  const { channels } = useChannels()

  // Form for order
  const { register, handleSubmit, watch, setValue, reset, trigger, formState: { errors } } = useForm({
    mode: 'onChange', // 值变更即校验，填入正确值后错误提示立即消失（不必再点下一步）
    defaultValues: {
      // 发件人信息
      senderName: user?.real_name || '',
      senderPhone: user?.phone || '',
      senderEmail: user?.email || '',
      senderCountry: '中国',
      senderProvince: '',
      senderCity: '',
      senderDistrict: '',
      senderStreet: '',
      senderAddress: '',
      senderPickup: '',
      // 收件人信息
      receiverName: '',
      receiverPhone: '',
      receiverEmail: '',
      receiverCountry: '',
      receiverCity: '',
      receiverZipcode: '',
      receiverAddress: '',
      receiverAddress2: '',
      receiverDelivery: '',
      // 货物信息
      itemType: '',
      itemName: '',
      otherItemName: '',
      cargoDescription: '',
      weight: '',
      length: '',
      width: '',
      height: '',
      quantity: 1,
      declaredValue: '',
      // 运输方案
      shippingMethod: '', // 拼柜：运货方案
      transportMode: '', // 整柜：运货方式（海运整柜/空运整柜）
      containerType: '', // 整柜：柜型
      insuranceRequired: false,
      packingRequired: false,
      remark: '',
    }
  })

  // Watch form values for price calculation
  const watchWeight = watch('weight')
  const watchShippingMethod = watch('shippingMethod')
  const watchTransportMode = watch('transportMode')
  const watchContainerType = watch('containerType')
  const watchReceiverCountry = watch('receiverCountry')
  const watchSenderProvince = watch('senderProvince')
  const watchSenderCity = watch('senderCity')
  const watchSenderDistrict = watch('senderDistrict')

  const getOptionLabel = (options, value) => {
    const found = options.find((opt) => opt.value === value)
    return found ? found.label : ''
  }

  const watchItemType = watch('itemType')
  const watchItemName = watch('itemName')
  const watchOtherItemName = watch('otherItemName')

  const itemTypeOptions = [
    ...Object.keys(itemTypeNames).map((key) => ({ value: key, label: key })),
    { value: '其他', label: '其他' },
  ]
  const itemNameOptions = (!watchItemType || watchItemType === '其他')
    ? []
    : (itemTypeNames[watchItemType] || []).map((name) => ({ value: name, label: name }))

  // Fetch LCL orders
  const { data: orders, isLoading } = useQuery({
    queryKey: ['lclOrders'],
    queryFn: () => lclService.getLCLOrders({ limit: 20 }),
    enabled: isAuthenticated,
  })

  // Fetch available shipments
  const { data: shipments } = useQuery({
    queryKey: ['availableShipments'],
    queryFn: () => lclService.getAvailableShipments({ destinationCountry: '', shippingMethod: '' }),
  })

  /*
   * 运费估算（拼柜：单价×重量；整柜：按柜型指示价）——最终以客服验货后的价格为准。
   *
   * 用 useCallback 锁住引用：它只读下面这几个 watch 值与 channels，而调用它的 useEffect
   * 依赖数组本来就把这些全列了。不锁引用的话，把 calculatePrice 直接写进 effect 依赖
   * （exhaustive-deps 会这么建议）会让它每次渲染都重新执行并 setEstimatedPrice —— 白算一遍。
   * 见 src/test/hooks-deps.test.jsx 里对同类问题的说明。
   */
  const calculatePrice = useCallback(() => {
    if (isFcl) {
      const tm = watchTransportMode
      const ct = watchContainerType
      const price = FCL_RATES[tm]?.[ct]
      if (!tm || !ct || price == null) {
        setEstimatedPrice(null)
        return
      }
      setEstimatedPrice({
        fcl: true,
        transportMode: tm,
        containerType: ct,
        freight: price.toFixed(2),
      })
      return
    }
    const weight = parseFloat(watchWeight) || 0
    const method = watchShippingMethod
    if (!weight || !method) {
      setEstimatedPrice(null)
      return
    }
    const channel = matchChannel(channels, method, watchReceiverCountry)
    const unitPrice = channel ? parseFloat(channel.unit_price || channel.base_price || 0) : 0
    setEstimatedPrice({
      method,
      hasRate: !!channel && unitPrice > 0,
      unitPrice: unitPrice.toFixed(2),
      weight: weight.toFixed(2),
      freight: (unitPrice * weight).toFixed(2),
      estimatedDays: channel?.estimated_days || '',
    })
  }, [isFcl, watchTransportMode, watchContainerType, watchWeight, watchShippingMethod, watchReceiverCountry, channels])

  // 自动计算运费（拼柜：单价×重量；整柜：按柜型）
  useEffect(() => {
    calculatePrice()
  }, [calculatePrice])

  // 切换步骤时：移除按钮焦点（避免「下一步」聚焦后回车/再次点击误触发提交）并滚动到该步顶部
  useEffect(() => {
    if (!showForm) return
    lastStepChangeAt.current = Date.now() // 记录切步时间，用于拦截切步后的误触发提交
    if (typeof document !== 'undefined' && document.activeElement?.blur) {
      document.activeElement.blur()
    }
    const el = stepTopRef.current
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 90
      window.scrollTo({ top: y, behavior: 'smooth' })
    }
  }, [currentStep, showForm])

  // 表单提交拦截：切换步骤后 600ms 内的提交视为「下一步误触发」，直接忽略（避免连点/回车误提交并弹出必填校验）
  const handleFormSubmit = (e) => {
    if (Date.now() - lastStepChangeAt.current < 600) {
      e.preventDefault()
      return
    }
    handleSubmit(onSubmit)(e)
  }

  // 加载省份（行政区划根节点）
  useEffect(() => {
    bookingService.getRegionChildren('00')
      .then((res) => setProvinceOptions((res || []).map((i) => ({ value: i.code, label: i.name }))))
      .catch(() => setProvinceOptions([]))
  }, [])

  // 加载收件人目的国家（与 /consolidation 同源）
  useEffect(() => {
    bookingService.getDestinationCountries()
      .then((res) => {
        if (Array.isArray(res) && res.length > 0) {
          if (typeof res[0] === 'string') {
            setDestinationCountries(res.map((name) => ({ value: name, label: name })))
          } else {
            setDestinationCountries(res.map((item) => {
              const zh = item.country_name || ''
              const en = item.country_name_en || ''
              const display = zh && en ? `${zh} / ${en}` : zh
              return { value: display, label: display }
            }).filter((v) => v.value))
          }
        }
      })
      .catch(() => {})
  }, [])

  // 依据收件人国家加载物品类型/名称
  useEffect(() => {
    if (!watchReceiverCountry) {
      setItemTypeNames({})
      setValue('itemType', '')
      setValue('itemName', '')
      setValue('otherItemName', '')
      return
    }
    bookingService.getSensitiveGoodsOptions(watchReceiverCountry)
      .then((res) => {
        setItemTypeNames(res?.itemTypeNames || {})
        setValue('itemType', '')
        setValue('itemName', '')
        setValue('otherItemName', '')
      })
      .catch(() => setItemTypeNames({}))
  }, [watchReceiverCountry, setValue])

  // 敏感货检测（针对收件人国家 + 所选物品）
  useEffect(() => {
    if (!watchReceiverCountry || !watchItemType) {
      setSensitiveMessage('')
      return
    }
    const selectedItemName = watchItemType === '其他' ? watchOtherItemName : watchItemName
    if (!selectedItemName) {
      setSensitiveMessage('')
      return
    }
    bookingService.checkSensitiveGoods({
      destinationCountry: watchReceiverCountry,
      itemType: watchItemType,
      itemName: selectedItemName,
    })
      .then((res) => setSensitiveMessage(res?.sensitive
        ? '温馨提示：您所选择的渠道禁运敏感货物，若误寄敏感货将产生扣关、退件、罚款等风险，责任自负！'
        : ''))
      .catch(() => setSensitiveMessage(''))
  }, [watchReceiverCountry, watchItemType, watchItemName, watchOtherItemName])

  // 省份变化：加载城市；直辖市（城市名与省份同名）跳过城市，合并其下辖区县
  useEffect(() => {
    if (!watchSenderProvince) {
      setCityOptions([])
      setDistrictOptions([])
      setStreetOptions([])
      setIsMunicipality(false)
      return
    }
    setValue('senderCity', '')
    setValue('senderDistrict', '')
    setValue('senderStreet', '')
    const provinceName = getOptionLabel(provinceOptions, watchSenderProvince)
    bookingService.getRegionChildren(watchSenderProvince)
      .then((res) => {
        const cities = (res || []).map((i) => ({ value: i.code, label: i.name }))
        const municipality = cities.length > 0 && cities.every((c) => c.label === provinceName)
        setIsMunicipality(municipality)
        if (municipality) {
          setCityOptions([])
          setStreetOptions([])
          Promise.all(cities.map((c) => bookingService.getRegionChildren(c.value).catch(() => [])))
            .then((lists) => {
              setDistrictOptions(lists.flat().map((i) => ({ value: i.code, label: i.name })))
            })
        } else {
          setCityOptions(cities)
          setDistrictOptions([])
          setStreetOptions([])
        }
      })
      .catch(() => {
        setCityOptions([])
        setDistrictOptions([])
        setStreetOptions([])
        setIsMunicipality(false)
      })
  }, [watchSenderProvince, setValue, provinceOptions])

  // 城市变化：加载区县（非直辖市）
  useEffect(() => {
    if (isMunicipality) return
    if (!watchSenderCity) {
      setDistrictOptions([])
      setStreetOptions([])
      return
    }
    setValue('senderDistrict', '')
    setValue('senderStreet', '')
    bookingService.getRegionChildren(watchSenderCity)
      .then((res) => {
        setDistrictOptions((res || []).map((i) => ({ value: i.code, label: i.name })))
        setStreetOptions([])
      })
      .catch(() => setDistrictOptions([]))
  }, [watchSenderCity, isMunicipality, setValue])

  // 区县变化：加载街道/城镇
  useEffect(() => {
    if (!watchSenderDistrict) {
      setStreetOptions([])
      return
    }
    setValue('senderStreet', '')
    bookingService.getRegionChildren(watchSenderDistrict)
      .then((res) => setStreetOptions((res || []).map((i) => ({ value: i.code, label: i.name }))))
      .catch(() => setStreetOptions([]))
  }, [watchSenderDistrict, setValue])


  // Create order mutation
  const createOrderMutation = useMutation({
    mutationFn: (data) => bookingService.createBooking({
      ...data,
      booking_type: 'lcl',
    }),
    // 接线后把参数加回来（onSuccess: (result) =>）并用 setSubmittedOrder({ code: result.code, ... })
    // 替换 onSubmit 里那段本地占位，见 onSubmit 上方注释。
    onSuccess: () => {
      toast.success('订单创建成功，可以在【我的订单】中查看')
      setShowForm(false)
      reset()
      setCurrentStep(1)
      setEstimatedPrice(null)
      setCargoConfirmed(false)
      queryClient.invalidateQueries(['lclOrders'])
    },
    onError: (error) => {
      toast.error(error.message || '创建订单失败')
    }
  })

  // Form submit（当前仅前端演示，不写库；后端 LCL 订单接口就绪后可改回 createOrderMutation.mutate(...)）
  const onSubmit = (data) => {
    // 仅最后一步(运输方案)才真正提交；其它步骤按回车等价于「下一步」，避免回车误触发提交而跳过第4步
    if (currentStep < 4) {
      handleNext()
      return
    }
    const selectedItemName = data.itemType === '其他' ? data.otherItemName : data.itemName
    const transportText = isFcl
      ? [data.transportMode, data.containerType].filter(Boolean).join(' / ')
      : data.shippingMethod
    setSubmittedOrder({
      // 本模块不写库（见上方注释），这里【不要】再用 Math.random() 编一个 DD-2026-XXXXXX：
      // 那看起来跟真编号一模一样，用户会拿它去「我的订单」查，而库里根本没有这单。
      // 给一个明确的占位符，接上后端接口后改成 result 里的真编号。
      code: null,
      itemName: selectedItemName,
      weight: data.weight,
      quantity: data.quantity,
      receiverName: data.receiverName,
      destinationCountry: data.receiverCountry,
      transportText,
      freight: estimatedPrice?.freight,
    })
    toast.success('需求已提交，客服会尽快与您联系')
  }

  // 复制订单编号
  const copyOrderCode = (code) => {
    navigator.clipboard?.writeText(code)
    toast.success('订单编号已复制')
  }

  // 继续下单：清空成功态并重置表单
  const handleContinueOrder = () => {
    setSubmittedOrder(null)
    setShowForm(false)
    reset()
    setCurrentStep(1)
    setEstimatedPrice(null)
    setCargoConfirmed(false)
  }

  // 下一步：第1步（发件人信息）先校验必填项再进入下一步
  const handleNext = async () => {
    if (currentStep === 1) {
      const step1Fields = ['senderName', 'senderPhone', 'senderProvince', 'senderDistrict', 'senderStreet', 'senderAddress', 'senderPickup']
      const fields = isMunicipality ? step1Fields : ['senderCity', ...step1Fields]
      const valid = await trigger(fields)
      if (!valid) return
    }
    if (currentStep === 2) {
      const valid = await trigger(['receiverName', 'receiverPhone', 'receiverCountry', 'receiverCity', 'receiverZipcode', 'receiverAddress', 'receiverDelivery'])
      if (!valid) return
    }
    if (currentStep === 3) {
      const nameField = watchItemType === '其他' ? 'otherItemName' : 'itemName'
      const valid = await trigger(['itemType', nameField, 'quantity', 'weight', 'length', 'width', 'height', 'declaredValue'])
      if (!valid) return
    }
    setCurrentStep((prev) => prev + 1)
  }

  const steps = [
    { num: 1, title: '发件人信息', icon: FaUser },
    { num: 2, title: '收件人信息', icon: FaMapMarkerAlt },
    { num: 3, title: '货物信息', icon: FaBox },
    { num: 4, title: '运输方案', icon: FaTruck },
  ]

  // 下单成功页（参考集运报名成功页）
  if (submittedOrder) {
    return (
      <Card className="max-w-2xl mx-auto">
        <Card.Body className="text-center py-12">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <FaCheckCircle className="w-10 h-10 text-green-500" />
          </div>
          <h3 className="text-fluid-2xl font-bold text-gray-900 mb-2">需求已记录</h3>
          <p className="text-gray-600 mb-8">
            您的{orderTypeName}需求已提交，客服会尽快与您联系确认
          </p>

          {/*
            code 为空 = 本模块还没接后端（不写库），此时【不能】显示编号与复制按钮 ——
            也不能说「可在我的订单中查看」，那里查不到这一单。接上接口后 code 有值，
            自动切回下面那套「编号 + 复制」的展示。
            编号是那时本页最重要的信息，小屏用 text-xl 防等宽字体折行。
          */}
          {submittedOrder.code ? (
            <div className="bg-gray-50 rounded-lg px-4 py-6 sm:p-6 mb-8">
              <p className="text-sm text-gray-500 mb-2">订单编号</p>
              <div className="flex items-center justify-center gap-2 sm:gap-3">
                <span className="text-fluid-3xl font-mono font-bold text-primary-600 whitespace-nowrap">{submittedOrder.code}</span>
                <button
                  type="button"
                  onClick={() => copyOrderCode(submittedOrder.code)}
                  className="flex items-center justify-center w-10 h-10 shrink-0 hover:bg-gray-200 rounded-lg transition-colors"
                  title="复制编号"
                  aria-label="复制订单编号"
                >
                  <FaCopy className="w-5 h-5 text-gray-500" />
                </button>
              </div>
            </div>
          ) : (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-4 mb-8 text-left text-sm text-amber-800">
              该功能尚在建设中，本次提交暂不生成订单编号，请留意客服联系。
            </div>
          )}

          <div className="grid grid-cols-fluid-sm gap-4 text-left mb-8">
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-500 mb-1">物品名称</p>
              <p className="font-medium">{submittedOrder.itemName || '-'}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-500 mb-1">重量</p>
              <p className="font-medium">{submittedOrder.weight} KG</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-500 mb-1">收件人 / 目的国家</p>
              <p className="font-medium">{submittedOrder.receiverName} / {submittedOrder.destinationCountry}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-500 mb-1">运输方案</p>
              <p className="font-medium">{submittedOrder.transportText}</p>
            </div>
            {submittedOrder.freight && (
              <div className="bg-gray-50 rounded-lg p-4 sm:col-span-2">
                <p className="text-sm text-gray-500 mb-1">预估运费</p>
                <p className="font-medium text-primary-600">¥{submittedOrder.freight}（以客服验货后的价格为准）</p>
              </div>
            )}
          </div>

          <div className="flex gap-4 justify-center">
            <Button variant="outline" onClick={handleContinueOrder}>继续下单</Button>
          </div>
        </Card.Body>
      </Card>
    )
  }

  return (
    <>
      {/* Create Order Button */}
      {!showForm && (
        <div className="mb-8 text-center">
          <Button
            size="lg"
            icon={<FaPlus />}
            onClick={() => setShowForm(true)}
            className="px-12"
          >
            {isFcl ? '创建整柜订单' : '创建散货拼柜订单'}
          </Button>
        </div>
      )}

      {/* Order Form */}
      {showForm && (
        <Card className="mb-8">
          <Card.Header>
            <div className="flex items-center justify-between">
              <h4 className="font-semibold">{isFcl ? '创建整柜订单' : '创建散货拼柜订单'}</h4>
              <button
                onClick={() => {
                  setShowForm(false)
                  reset()
                  setCurrentStep(1)
                  setEstimatedPrice(null)
                  setCargoConfirmed(false)
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <FaTimes className="w-5 h-5" />
              </button>
            </div>
          </Card.Header>
          <Card.Body>
            {/*
              Step Indicator

              撑爆的是连接线：原先死写 w-24(96px)+mx-4(32px)，四步整条 576px，320px 屏上
              第 3、4 步被 Card 的 overflow-hidden 裁掉，用户看不到自己在第几步。
              连接线改 flex-1 由剩余空间决定，是这里唯一必要的改动 —— 圆点尺寸不用动。
              步骤名用 text-fluid-xs 令牌随视口平滑缩放，而不是在 640px 上跳一档。
            */}
            <div ref={stepTopRef} className="flex items-center mb-8 scroll-mt-24">
              {steps.map((step, index) => {
                const Icon = step.icon
                const isActive = currentStep === step.num
                const isCompleted = currentStep > step.num
                return (
                  <div key={step.num} className={`flex items-center ${index < steps.length - 1 ? 'flex-1' : ''}`}>
                    <div className="flex flex-col items-center shrink-0">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
                        isActive ? 'bg-primary text-white' :
                        isCompleted ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'
                      }`}>
                        {isCompleted ? <FaCheck /> : <Icon className="w-5 h-5" />}
                      </div>
                      <span className={`mt-2 text-fluid-xs text-center whitespace-nowrap ${isActive ? 'text-primary font-medium' : 'text-gray-500'}`}>
                        {step.title}
                      </span>
                    </div>
                    {index < steps.length - 1 && (
                      <div className={`flex-1 h-1 mx-1.5 rounded ${
                        isCompleted ? 'bg-green-500' : 'bg-gray-200'
                      }`} />
                    )}
                  </div>
                )
              })}
            </div>

            <form onSubmit={handleFormSubmit}>
              {/* Step 1: 发件人信息 */}
              {currentStep === 1 && (
                <div className="max-w-2xl mx-auto space-y-6">
                  <h5 className="text-lg font-medium mb-4 flex items-center gap-2">
                    <FaUser className="text-primary" />
                    发件人信息
                  </h5>
                  <div className="grid grid-cols-fluid-md gap-4">
                    <Input
                      label="发件人姓名"
                      required
                      placeholder="请输入发件人姓名"
                      error={errors.senderName?.message}
                      {...register('senderName', { required: '请输入发件人姓名' })}
                    />
                    <Input
                      label="发件人手机号"
                      type="tel"
                      required
                      placeholder="请输入发件人手机号"
                      error={errors.senderPhone?.message}
                      {...register('senderPhone', {
                        required: '请输入发件人手机号',
                        pattern: { value: /^1[3-9]\d{9}$/, message: '请输入正确的手机号码' },
                      })}
                    />
                    <Input
                      label="邮箱"
                      type="email"
                      placeholder="请输入邮箱地址（选填）"
                      {...register('senderEmail')}
                    />
                    <Select
                      label="国家"
                      required
                      options={[{ value: '中国', label: '中国' }]}
                      {...register('senderCountry', { required: '请选择国家' })}
                      value={watch('senderCountry') || '中国'}
                    />
                  </div>

                  {/* 省 / 市 / 区 / 街道 级联（直辖市跳过城市） */}
                  <div className={`grid gap-4 ${isMunicipality ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
                    <Select
                      label="省份"
                      required
                      placeholder="请选择省份"
                      options={provinceOptions}
                      error={errors.senderProvince?.message}
                      {...register('senderProvince', { required: '请选择省份' })}
                      value={watchSenderProvince || ''}
                    />

                    {!isMunicipality && (
                      <Select
                        label="城市"
                        required
                        placeholder={watchSenderProvince ? '请选择城市' : '请先选择省份'}
                        options={cityOptions}
                        disabled={!watchSenderProvince}
                        error={errors.senderCity?.message}
                        {...register('senderCity', { required: isMunicipality ? false : '请选择城市' })}
                        value={watchSenderCity || ''}
                      />
                    )}

                    <Select
                      label="区域/县"
                      required
                      placeholder={(isMunicipality ? districtOptions.length > 0 : !!watchSenderCity)
                        ? '请选择区域/县'
                        : (isMunicipality ? '请先选择省份' : '请先选择城市')}
                      options={districtOptions}
                      disabled={isMunicipality ? districtOptions.length === 0 : !watchSenderCity}
                      error={errors.senderDistrict?.message}
                      {...register('senderDistrict', { required: '请选择区域/县' })}
                      value={watchSenderDistrict || ''}
                    />

                    <Select
                      label="街道/城镇"
                      required
                      placeholder={watchSenderDistrict ? '请选择街道/城镇' : '请先选择区域/县'}
                      options={streetOptions}
                      disabled={!watchSenderDistrict}
                      error={errors.senderStreet?.message}
                      {...register('senderStreet', { required: '请选择街道/城镇' })}
                      value={watch('senderStreet') || ''}
                    />
                  </div>

                  {/* 详细地址 */}
                  <Input
                    label="详细地址"
                    required
                    placeholder="请输入详细取件地址（门牌号、楼层等）"
                    error={errors.senderAddress?.message}
                    {...register('senderAddress', { required: '请输入详细地址' })}
                  />

                  {/* 是否需要上门取件 */}
                  <Select
                    label="是否需要上门取件"
                    required
                    placeholder="请选择是否需要上门取件"
                    options={[{ value: '1', label: '是' }, { value: '0', label: '否' }]}
                    error={errors.senderPickup?.message}
                    {...register('senderPickup', { required: '请选择是否需要上门取件' })}
                    value={watch('senderPickup') || ''}
                  />
                </div>
              )}

              {/* Step 2: 收件人信息 */}
              {currentStep === 2 && (
                <div className="max-w-2xl mx-auto space-y-6">
                  <h5 className="text-lg font-medium mb-4 flex items-center gap-2">
                    <FaMapMarkerAlt className="text-primary" />
                    收件人信息
                  </h5>
                  <div className="grid grid-cols-fluid-md gap-4">
                    <Input
                      label="收件人姓名"
                      required
                      placeholder="请输入收件人姓名"
                      error={errors.receiverName?.message}
                      {...register('receiverName', { required: '请输入收件人姓名' })}
                    />
                    <Input
                      label="收件人手机号"
                      type="tel"
                      required
                      placeholder="请输入收件人手机号"
                      error={errors.receiverPhone?.message}
                      {...register('receiverPhone', { required: '请输入收件人手机号' })}
                    />
                    <Input
                      label="收件人邮箱"
                      type="email"
                      placeholder="请输入收件人邮箱（选填）"
                      {...register('receiverEmail')}
                    />
                    <Select
                      label="国家"
                      required
                      placeholder="请选择国家"
                      options={destinationCountries}
                      error={errors.receiverCountry?.message}
                      {...register('receiverCountry', { required: '请选择国家' })}
                      value={watch('receiverCountry') || ''}
                    />
                    <Input
                      label="城市"
                      required
                      placeholder="请输入城市"
                      error={errors.receiverCity?.message}
                      {...register('receiverCity', { required: '请输入城市' })}
                    />
                    <Input
                      label="邮编"
                      required
                      placeholder="请输入邮编"
                      error={errors.receiverZipcode?.message}
                      {...register('receiverZipcode', { required: '请输入邮编' })}
                    />
                    <Input
                      label="详细地址1"
                      required
                      wrapperClassName="md:col-span-2"
                      placeholder="请输入详细地址1"
                      error={errors.receiverAddress?.message}
                      {...register('receiverAddress', { required: '请输入详细地址1' })}
                    />
                    <Input
                      label="详细地址2"
                      wrapperClassName="md:col-span-2"
                      placeholder="请输入详细地址2（选填）"
                      {...register('receiverAddress2')}
                    />
                    <Select
                      label="是否需要派送"
                      required
                      wrapperClassName="md:col-span-2"
                      placeholder="请选择是否需要派送"
                      options={[{ value: '1', label: '是' }, { value: '0', label: '否' }]}
                      error={errors.receiverDelivery?.message}
                      {...register('receiverDelivery', { required: '请选择是否需要派送' })}
                      value={watch('receiverDelivery') || ''}
                    />
                  </div>
                </div>
              )}

              {/* Step 3: 货物信息 */}
              {currentStep === 3 && (
                <div className="max-w-2xl mx-auto space-y-6">
                  <h5 className="text-lg font-medium mb-4 flex items-center gap-2">
                    <FaBox className="text-primary" />
                    货物信息
                  </h5>
                  <div className="grid grid-cols-fluid-md gap-4">
                    <Select
                      label="物品类型"
                      required
                      placeholder={watchReceiverCountry ? '请选择物品类型' : '请先选择收件人国家'}
                      options={itemTypeOptions}
                      disabled={!watchReceiverCountry}
                      error={errors.itemType?.message}
                      {...register('itemType', { required: '请选择物品类型' })}
                      value={watchItemType || ''}
                    />
                    {watchItemType === '其他' ? (
                      <Input
                        label="物品名称"
                        required
                        placeholder="可输入多个物品名称，用逗号分隔"
                        error={errors.otherItemName?.message}
                        {...register('otherItemName', { required: '请输入物品名称' })}
                      />
                    ) : (
                      <Select
                        label="物品名称"
                        required
                        placeholder={!watchReceiverCountry ? '请先选择收件人国家' : (!watchItemType ? '请先选择物品类型' : '请选择物品名称')}
                        options={itemNameOptions}
                        disabled={!watchReceiverCountry || !watchItemType}
                        error={errors.itemName?.message}
                        {...register('itemName', { required: '请选择物品名称' })}
                        value={watchItemName || ''}
                      />
                    )}
                  </div>

                  {sensitiveMessage && (
                    <div className="rounded-lg border border-orange-300 bg-orange-50 p-4 text-orange-800 text-sm flex items-start gap-2">
                      <FaExclamationTriangle className="mt-0.5 shrink-0" />
                      <span>{sensitiveMessage}</span>
                    </div>
                  )}

                  <div className="grid md:grid-cols-3 gap-4">
                    <Input
                      label="货物件数"
                      type="number"
                      min="1"
                      required
                      placeholder="请输入件数"
                      error={errors.quantity?.message}
                      {...register('quantity', { required: '请输入件数', min: { value: 1, message: '件数至少为 1' } })}
                    />
                    <Input
                      label="货物重量 (KG)"
                      type="number"
                      step="0.01"
                      required
                      placeholder="请输入重量"
                      error={errors.weight?.message}
                      {...register('weight', { required: '请输入重量', min: { value: 0.01, message: '重量必须大于 0' } })}
                    />
                    <Input
                      label="物品价值 (USD)"
                      type="number"
                      step="0.01"
                      required
                      placeholder="请输入物品价值"
                      error={errors.declaredValue?.message}
                      {...register('declaredValue', { required: '请输入物品价值', min: { value: 0, message: '价值不能小于 0' } })}
                    />
                  </div>

                  <div>
                    <p className="block mb-1.5 font-medium text-gray-700 text-sm">
                      货物体积 (cm)<span className="text-red-500 ml-1">*</span>
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <Input
                        type="number"
                        min="0"
                        step="0.1"
                        placeholder="长"
                        error={errors.length?.message}
                        {...register('length', { required: '请输入长', min: { value: 0, message: '不能为负' } })}
                      />
                      <Input
                        type="number"
                        min="0"
                        step="0.1"
                        placeholder="宽"
                        error={errors.width?.message}
                        {...register('width', { required: '请输入宽', min: { value: 0, message: '不能为负' } })}
                      />
                      <Input
                        type="number"
                        min="0"
                        step="0.1"
                        placeholder="高"
                        error={errors.height?.message}
                        {...register('height', { required: '请输入高', min: { value: 0, message: '不能为负' } })}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block mb-1.5 font-medium text-gray-700 text-sm">备注</label>
                    <textarea
                      className="form-control"
                      rows="2"
                      placeholder="选填，如材质、用途等补充说明"
                      {...register('cargoDescription')}
                    />
                  </div>
                </div>
              )}

              {/* Step 4: 运输方案 */}
              {currentStep === 4 && (
                <div className="grid lg:grid-cols-2 gap-8">
                  <div className="space-y-6">
                    <h5 className="text-lg font-medium mb-4 flex items-center gap-2">
                      <FaTruck className="text-primary" />
                      运输方案
                    </h5>

                    {isFcl ? (
                      <>
                        <Select
                          label="运货方式"
                          required
                          placeholder="请选择运货方式"
                          options={FCL_TRANSPORT_MODES}
                          error={errors.transportMode?.message}
                          {...register('transportMode', { required: '请选择运货方式' })}
                          value={watchTransportMode || ''}
                        />
                        <Select
                          label="柜型"
                          required
                          placeholder="请选择柜型"
                          options={FCL_CONTAINER_TYPES}
                          error={errors.containerType?.message}
                          {...register('containerType', { required: '请选择柜型' })}
                          value={watchContainerType || ''}
                        />
                        <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                          <input type="checkbox" {...register('insuranceRequired')} className="w-5 h-5 text-primary rounded" />
                          <div className="flex-1">
                            <span className="font-medium">是否需要保价</span>
                            <p className="text-xs text-gray-500">为货物提供保价服务，保障货物安全</p>
                          </div>
                        </label>
                      </>
                    ) : (
                      <>
                        <Select
                          label="运货方案"
                          required
                          placeholder="请选择运货方案"
                          options={SHIPPING_METHODS}
                          error={errors.shippingMethod?.message}
                          {...register('shippingMethod', { required: '请选择运货方案' })}
                          value={watchShippingMethod || ''}
                        />
                        <div className="space-y-3">
                          <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                            <input type="checkbox" {...register('insuranceRequired')} className="w-5 h-5 text-primary rounded" />
                            <div className="flex-1">
                              <span className="font-medium">是否需要保价</span>
                              <p className="text-xs text-gray-500">为货物提供保价服务，保障货物安全</p>
                            </div>
                          </label>
                          <div>
                            <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                              <input type="checkbox" {...register('packingRequired')} className="w-5 h-5 text-primary rounded" />
                              <div className="flex-1">
                                <span className="font-medium">代打包服务</span>
                                <p className="text-xs text-gray-500">专业打包，保护货物运输安全</p>
                              </div>
                            </label>
                            <p className="text-xs text-yellow-600 mt-1.5 pl-1">代打包服务收费以实际收费</p>
                          </div>
                        </div>
                      </>
                    )}

                    <div>
                      <label className="block mb-1.5 font-medium text-gray-700 text-sm">备注</label>
                      <textarea
                        className="form-control"
                        rows="3"
                        placeholder="如有特殊要求请备注说明（选填）"
                        {...register('remark')}
                      />
                    </div>
                  </div>

                  {/* 运费估算 */}
                  <div>
                    <Card className="bg-gradient-to-br from-primary/5 to-secondary/5 sticky top-4">
                      <Card.Header>
                        <h5 className="font-semibold flex items-center gap-2">
                          <FaCalculator className="text-primary" />
                          运费估算
                        </h5>
                      </Card.Header>
                      <Card.Body>
                        {estimatedPrice ? (
                          <div className="space-y-4">
                            <div className="text-center p-6 bg-white rounded-xl">
                              <p className="text-sm text-gray-500 mb-1">预估运费</p>
                              <p className="text-fluid-4xl font-bold text-primary break-all">¥{estimatedPrice.freight}</p>
                              {!estimatedPrice.fcl && (
                                <p className="text-xs text-gray-500 mt-2">
                                  运费 = 单价 ¥{estimatedPrice.unitPrice}/KG × {estimatedPrice.weight} KG
                                </p>
                              )}
                            </div>

                            <div className="space-y-3 text-sm">
                              {estimatedPrice.fcl ? (
                                <>
                                  <div className="flex justify-between py-2 border-b">
                                    <span className="text-gray-500">运货方式</span>
                                    <span className="font-medium">{estimatedPrice.transportMode}</span>
                                  </div>
                                  <div className="flex justify-between py-2">
                                    <span className="text-gray-500">柜型</span>
                                    <span className="font-medium">{estimatedPrice.containerType}</span>
                                  </div>
                                </>
                              ) : (
                                <>
                                  <div className="flex justify-between py-2 border-b">
                                    <span className="text-gray-500">运货方案</span>
                                    <span className="font-medium">{estimatedPrice.method}</span>
                                  </div>
                                  <div className="flex justify-between py-2 border-b">
                                    <span className="text-gray-500">单价</span>
                                    <span>¥{estimatedPrice.unitPrice}/KG</span>
                                  </div>
                                  <div className="flex justify-between py-2 border-b">
                                    <span className="text-gray-500">重量</span>
                                    <span>{estimatedPrice.weight} KG</span>
                                  </div>
                                  {estimatedPrice.estimatedDays && (
                                    <div className="flex justify-between py-2">
                                      <span className="text-gray-500">预计时效</span>
                                      <span>{estimatedPrice.estimatedDays}</span>
                                    </div>
                                  )}
                                </>
                              )}
                            </div>

                            <p className="text-xs text-gray-500 text-center pt-3 border-t">以客服验货后的价格为准</p>
                          </div>
                        ) : (
                          <div className="text-center py-8">
                            <FaCalculator className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                            <p className="text-gray-500">{isFcl ? '选择运货方式和柜型后自动计算运费' : '选择运货方案后自动计算运费'}</p>
                            <p className="text-xs text-gray-400 mt-2">以客服验货后的价格为准</p>
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex justify-between mt-8 pt-6 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setCurrentStep(prev => Math.max(1, prev - 1))}
                  disabled={currentStep === 1}
                >
                  上一步
                </Button>

                {currentStep < 4 ? (
                  <Button
                    type="button"
                    onClick={handleNext}
                  >
                    下一步
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    loading={createOrderMutation.isPending}
                    icon={<FaCheck />}
                  >
                    提交订单
                  </Button>
                )}
              </div>
            </form>
          </Card.Body>
        </Card>
      )}

      {/* Available Shipments（仅拼柜展示可拼船期） */}
      {!isFcl && !showForm && (
        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-4">可拼船期</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {shipments?.length > 0 ? (
              shipments.map((shipment, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <Card.Body>
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h4 className="font-semibold">{shipment.name}</h4>
                        <p className="text-sm text-gray-500">{shipment.destination_country}</p>
                      </div>
                      <StatusBadge status={shipment.status} />
                    </div>

                    <div className="space-y-2 mb-4">
                      <div className="flex items-center gap-2 text-sm">
                        <FaClock className="text-gray-400" />
                        <span>截单: {formatDateTime(shipment.cutoff_date, 'MM-DD')}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <FaRoute className="text-gray-400" />
                        <span>预计时效: {shipment.transit_days}天</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <FaDollarSign className="text-gray-400" />
                        <span>单价: ¥{shipment.unit_price}/KG</span>
                      </div>
                    </div>

                    <div className="pt-4 border-t">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">已拼</span>
                        <span className="font-medium">{shipment.booked_volume} / {shipment.total_volume} CBM</span>
                      </div>
                      <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary-500 rounded-full transition-all"
                          style={{ width: `${(shipment.booked_volume / shipment.total_volume) * 100}%` }}
                        />
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              ))
            ) : (
              <div className="col-span-full text-center py-8 text-gray-500">
                暂无可用船期
              </div>
            )}
          </div>
        </div>
      )}

      {/* My Orders */}
      {isAuthenticated && !showForm && (
        <Card>
          <Card.Header>
            <h5 className="font-semibold">我的{orderTypeName}订单</h5>
          </Card.Header>
          <Card.Body className="p-0">
            {isLoading ? (
              <LoadingOverlay />
            ) : orders?.length > 0 ? (
              <>
              {/* 小屏卡片视图 */}
              <div className="md:hidden space-y-3 p-4">
                {orders.map((order) => (
                  <MobileCard
                    key={order.name}
                    title={order.name}
                    badge={<StatusBadge status={order.status} />}
                    fields={[
                      { label: '船期', value: order.shipment },
                      { label: '货物名称', value: order.cargo_name },
                      {
                        label: '重量/体积',
                        value: `${order.weight} KG${order.volume ? ` / ${order.volume} CBM` : ''}`,
                      },
                      {
                        label: '金额',
                        value: order.total_amount
                          ? <span className="font-medium text-primary-600">¥{order.total_amount.toFixed(2)}</span>
                          : null,
                      },
                      { label: '创建时间', value: formatDateTime(order.creation, 'MM-DD HH:mm') },
                    ]}
                  />
                ))}
              </div>

              <div className="hidden md:block overflow-x-auto">
                <table className="table">
                  <thead>
                    <tr>
                      <th>订单号</th>
                      <th>船期</th>
                      <th>货物名称</th>
                      <th>重量/体积</th>
                      <th>金额</th>
                      <th>状态</th>
                      <th>创建时间</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order) => (
                      <tr key={order.name}>
                        <td className="font-medium">{order.name}</td>
                        <td>{order.shipment}</td>
                        <td>{order.cargo_name}</td>
                        <td>
                          {order.weight} KG
                          {order.volume && <span className="text-gray-400"> / {order.volume} CBM</span>}
                        </td>
                        <td>
                          {order.total_amount ? (
                            <span className="font-medium text-primary-600">
                              ¥{order.total_amount.toFixed(2)}
                            </span>
                          ) : '-'}
                        </td>
                        <td><StatusBadge status={order.status} /></td>
                        <td className="text-gray-500 text-sm">
                          {formatDateTime(order.creation, 'MM-DD HH:mm')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              </>
            ) : (
              <EmptyState
                title={`暂无${orderTypeName}订单`}
                description={`创建您的第一个${orderTypeName}订单`}
              />
            )}
          </Card.Body>
        </Card>
      )}

      {/* 进入货物信息前的「如实填写」确认弹框（未确认前不可填写） */}
      <Modal open={showForm && currentStep === 3 && !cargoConfirmed} closable={false}>
        <div className="text-center">
          <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FaExclamationTriangle className="w-7 h-7 text-amber-500" />
          </div>
          <h4 className="text-lg font-semibold text-gray-900 mb-2">温馨提示</h4>
          <p className="text-gray-600 mb-6 leading-relaxed">请如实填写货物信息，如有瞒报，仓库有权拒收</p>
          <Button className="w-full" onClick={() => setCargoConfirmed(true)}>确认</Button>
        </div>
      </Modal>
    </>
  )
}
